import { useEffect, useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { SearchableSelect } from '../../../components/SearchableSelect'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { EmailSignature, Part, Vendor, WorkOrder, WorkOrderPart } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { usePermissions } from '../../../hooks/usePermissions'
import { useAuthStore } from '../../../store/authStore'

interface Props {
  workOrder: WorkOrder
  parts: WorkOrderPart[]
  editable: boolean
}

export function PartsPanel({ workOrder, parts, editable }: Props) {
  const qc = useQueryClient()
  const { formatCurrency } = useOrgSettings()
  const [addOpen, setAddOpen] = useState(false)
  const isTask = workOrder.kind === 'task'
  const client = isTask ? api.tasks : api.workOrders

  const removeM = useMutation({
    mutationFn: (partId: string) => client.removePart(workOrder.id, partId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wo-parts', workOrder.id] }); toast.success('Part removed') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const totalCost = parts.reduce((sum, p) => sum + (Number(p.unit_cost) || 0) * p.quantity, 0)

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          Parts & materials {parts.length > 0 && `(${parts.length})`}
        </Typography>
        {editable && (
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setAddOpen(true)}
            sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>Add part</Button>
        )}
      </Stack>

      {parts.length > 0 ? (
        <>
          <Stack spacing={1}>
            {parts.map((p) => {
              const lowStock = !p.deducted // we can only check at this level
              return (
                <Stack key={p.id} direction="row" alignItems="center" spacing={1.5}
                  sx={{ px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{p.part_name}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>
                        {p.sku ?? p.part_code}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>·</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        x{p.quantity} {p.unit_cost != null ? `· ${formatCurrency(Number(p.unit_cost) * p.quantity)}` : ''}
                      </Typography>
                    </Stack>
                  </Box>
                  {p.deducted && <Chip label="Deducted" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#05966918', color: '#059669' }} />}
                  {editable && (
                    <Tooltip title="Remove part" arrow>
                      <IconButton size="small" onClick={() => removeM.mutate(p.part_id)}
                        disabled={removeM.isPending}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )
            })}
          </Stack>
          {totalCost > 0 && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mt: 1.5, textAlign: 'right' }}>
              Total: {formatCurrency(totalCost)}
            </Typography>
          )}
        </>
      ) : (
        <Box sx={{ py: 2.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No parts tagged yet</Typography>
        </Box>
      )}

      {addOpen && <AddPartDialog workOrderId={workOrder.id} isTask={isTask} onClose={() => setAddOpen(false)}
        onAdded={() => { qc.invalidateQueries({ queryKey: ['wo-parts', workOrder.id] }); setAddOpen(false) }} />}
    </Box>
  )
}

function AddPartDialog({ workOrderId, isTask, onClose, onAdded }: { workOrderId: string; isTask: boolean; onClose: () => void; onAdded: () => void }) {
  const client = isTask ? api.tasks : api.workOrders
  const { can } = usePermissions()
  const [partId, setPartId] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [reorderPart, setReorderPart] = useState<Part | null>(null)

  const partsQ = useQuery({ queryKey: ['parts'], queryFn: () => api.parts.list({}), staleTime: 60_000 })
  const allParts: Part[] = Array.isArray(partsQ.data) ? partsQ.data : []

  const selectedPart = allParts.find((p) => p.id === partId)
  const needsMore = selectedPart && selectedPart.quantity < (parseInt(quantity) || 1)

  const addM = useMutation({
    mutationFn: () => client.addPart(workOrderId, { part_id: partId, quantity: parseInt(quantity) || 1 }),
    onSuccess: () => { toast.success('Part added'); onAdded() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add part to {isTask ? 'task' : 'work order'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <SearchableSelect
              value={partId}
              onChange={setPartId}
              options={allParts}
              getValue={(p) => p.id}
              getLabel={(p) => p.name}
              placeholder="Select a part"
              renderOption={(p) => (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                  <Typography sx={{ fontSize: 13, flex: 1 }}>{p.name}</Typography>
                  <Chip label={`${p.quantity} in stock`} size="small"
                    sx={{ height: 18, fontSize: 10, fontWeight: 600,
                      bgcolor: p.quantity === 0 ? '#DC262618' : p.quantity <= p.min_quantity ? '#D9770618' : '#05966918',
                      color: p.quantity === 0 ? '#DC2626' : p.quantity <= p.min_quantity ? '#D97706' : '#059669' }} />
                </Stack>
              )}
            />
            <TextField label="Quantity needed" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)}
              size="small" fullWidth inputProps={{ min: 1 }} />

            {needsMore && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, borderRadius: 1.5,
                bgcolor: (t) => `${t.palette.warning.main}08`, border: '1px solid', borderColor: (t) => `${t.palette.warning.main}30` }}>
                <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: '#D97706' }} />
                <Typography sx={{ fontSize: 12, color: '#D97706', fontWeight: 500 }}>
                  Only {selectedPart.quantity} in stock — you need {quantity}
                </Typography>
              </Stack>
            )}

            {needsMore && selectedPart.vendor_name && (
              <Tooltip title={!can('inventory:reorder') ? "You don't have permission to reorder" : `Send a reorder email to ${selectedPart.vendor_name}`} arrow>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <Button size="small" variant="outlined" startIcon={<LocalShippingOutlinedIcon sx={{ fontSize: 14 }} />}
                    disabled={!can('inventory:reorder')}
                    onClick={() => setReorderPart(selectedPart)}
                    sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
                    Reorder from {selectedPart.vendor_name}
                  </Button>
                </Box>
              </Tooltip>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={!partId || addM.isPending || !!needsMore} onClick={() => addM.mutate()}>
            {addM.isPending ? 'Adding...' : 'Add part'}
          </Button>
        </DialogActions>
      </Dialog>

      {reorderPart && <ReorderDialog part={reorderPart} onClose={() => setReorderPart(null)} />}
    </>
  )
}

// ─── Reorder from vendor dialog ──────────────────────────────────────────────

function ReorderDialog({ part, onClose }: { part: Part; onClose: () => void }) {
  const { user } = useAuthStore()
  const { org } = useOrgSettings()
  const prefs = (user?.preferences ?? {}) as Record<string, any>
  const signatures: EmailSignature[] = Array.isArray(prefs.signatures) ? prefs.signatures : []
  const defaultSig = signatures.find((s) => s.isDefault) ?? signatures[0]

  const vendorQ = useQuery({
    queryKey: ['vendor', part.vendor_id],
    queryFn: () => api.vendors.get(part.vendor_id!),
    enabled: !!part.vendor_id,
  })
  const vendor: Vendor | null = vendorQ.data ?? null

  const suggestedQty = Math.max((part.min_quantity * 2) - part.quantity, part.min_quantity)

  const [to, setTo] = useState('')
  const [subject, setSubject] = useState(`Reorder request: ${part.name} (${part.sku ?? part.part_code})`)
  const [body, setBody] = useState('')
  const [sigId, setSigId] = useState(defaultSig?.id ?? '')

  useEffect(() => {
    if (vendor?.email && !to) setTo(vendor.email)
  }, [vendor])

  useEffect(() => {
    const contactName = vendor?.contact_name ?? vendor?.name ?? 'there'
    setBody(
`Hi ${contactName},

We'd like to place a reorder for the following part:

Part: ${part.name}
SKU: ${part.sku ?? part.part_code}
Current stock: ${part.quantity} units
Suggested order: ${suggestedQty} units

Please confirm availability and lead time.

Thanks,
${user?.name ?? ''}`
    )
  }, [vendor, part])

  const selectedSig = signatures.find((s) => s.id === sigId)

  const sendM = useMutation({
    mutationFn: () => api.parts.sendReorderEmail(part.id, {
      to, subject, message: body,
      signature: selectedSig?.body,
      org_name: org?.name,
      sender_name: user?.name,
    }),
    onSuccess: () => { toast.success('Reorder email sent!'); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to send email'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Reorder from vendor</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
            bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
            <Stack spacing={1}>
              {[
                ['Part', part.name],
                ['SKU', part.sku ?? part.part_code],
              ].map(([label, val]) => (
                <Stack key={label} direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, ...(label === 'SKU' ? { fontFamily: 'ui-monospace, monospace' } : {}) }}>{val}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Current stock</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600,
                  color: part.quantity === 0 ? '#DC2626' : part.quantity <= part.min_quantity ? '#D97706' : 'text.primary' }}>
                  {part.quantity} units
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Suggested order</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>{suggestedQty} units</Typography>
              </Stack>
            </Stack>
          </Box>

          <Tooltip title="The vendor's email address — this is where the reorder request will be sent" arrow placement="top-start">
            <TextField label="Send to (email)" value={to} onChange={(e) => setTo(e.target.value)} size="small" fullWidth
              placeholder="vendor@example.com" type="email"
              helperText={!vendor?.email && part.vendor_name ? `No email on file for ${part.vendor_name} — enter one manually` : undefined} />
          </Tooltip>

          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} size="small" fullWidth />

          <TextField label="Message" value={body} onChange={(e) => setBody(e.target.value)} size="small" fullWidth multiline minRows={6}
            helperText="You can edit the message before sending" />

          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>
              Signature
            </Typography>
            {signatures.length > 0 ? (
              <>
                <SearchableSelect
                  value={sigId}
                  onChange={setSigId}
                  options={signatures}
                  getValue={(s) => s.id}
                  getLabel={(s) => `${s.name}${s.isDefault ? ' (default)' : ''}`}
                  placeholder="No signature"
                />
                {selectedSig && (
                  <Box sx={{ mt: 1.5, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                    bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Preview</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selectedSig.body}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No signatures created yet.</Typography>
                <Button size="small" onClick={onClose} sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>Create one in Settings</Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!to.trim() || !subject.trim() || !body.trim() || sendM.isPending}
          onClick={() => sendM.mutate()}
          startIcon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}>
          {sendM.isPending ? 'Sending...' : 'Send reorder email'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
